import { useState, useEffect } from 'react'
import { Target, CheckCircle, AlertTriangle, BarChart3, Plus, Edit2, Trash2, X } from 'lucide-react'
import { useFilters } from '../contexts/FilterContext'
import { api } from '../services/api'
import styles from './Metas.module.css'

interface Meta {
  id: number
  titulo: string
  descricao: string
  metrica: 'vendas' | 'leads' | 'pre_cadastros' | 'reservas'
  valor_meta: number
  valor_atual: number
  periodo: string
  data_inicio: string
  data_fim: string
  status: 'ativa' | 'pausada' | 'concluida' | 'cancelada'
  tipo_atribuicao: 'geral' | 'empreendimento' | 'corretor' | 'time'
  linha_negocio: string
}

const metricLabels: Record<string, string> = {
  vendas: 'Vendas',
  leads: 'Leads',
  pre_cadastros: 'Pre-Cadastros',
  reservas: 'Reservas',
}

const statusLabels: Record<string, string> = {
  ativa: 'Ativa',
  pausada: 'Pausada',
  concluida: 'Concluida',
  cancelada: 'Cancelada',
}

const periodoLabels: Record<string, string> = {
  diaria: 'Diaria',
  semanal: 'Semanal',
  quinzenal: 'Quinzenal',
  mensal: 'Mensal',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
}

export function Metas() {
  const { filterOptions } = useFilters()
  const [metas, setMetas] = useState<Meta[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingMeta, setEditingMeta] = useState<Meta | null>(null)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterMetrica, setFilterMetrica] = useState('')
  const [filterPeriodo, setFilterPeriodo] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  // Form state
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    metrica: 'vendas',
    valor_meta: 0,
    periodo: 'mensal',
    data_inicio: '',
    data_fim: '',
    tipo_atribuicao: 'geral',
    linha_negocio: '',
    status: 'ativa',
  })

  // Função para mapear dados da API para o formato do frontend
  const mapApiToMeta = (m: any): Meta => ({
    id: m.id,
    titulo: m.titulo,
    descricao: m.descricao || '',
    metrica: m.metric_type || m.metrica || 'vendas',
    valor_meta: m.valor_meta || 0,
    valor_atual: m.valor_atual || 0,
    periodo: m.period_type || m.periodo || 'mensal',
    data_inicio: m.data_inicio,
    data_fim: m.data_fim,
    status: m.status || 'ativa',
    tipo_atribuicao: m.assignment_type || m.tipo_atribuicao || 'geral',
    linha_negocio: m.linha_negocio || '',
  })

  // Função para mapear dados do formulário para o formato da API
  const mapFormToApi = (form: typeof formData) => ({
    titulo: form.titulo,
    descricao: form.descricao,
    metric_type: form.metrica,
    valor_meta: form.valor_meta,
    period_type: form.periodo,
    data_inicio: form.data_inicio,
    data_fim: form.data_fim,
    assignment_type: form.tipo_atribuicao,
    linha_negocio: form.linha_negocio || null,
    status: form.status,
  })

  // Função para carregar metas da API
  const loadMetas = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.getMetas()
      const mappedMetas: Meta[] = data.map(mapApiToMeta)
      setMetas(mappedMetas)
    } catch (err) {
      setError('Erro ao carregar metas')
      setMetas([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMetas()
  }, [])

  const filteredMetas = metas.filter((meta) => {
    if (filterStatus && meta.status !== filterStatus) return false
    if (filterMetrica && meta.metrica !== filterMetrica) return false
    if (filterPeriodo && meta.periodo !== filterPeriodo) return false
    if (searchTerm && !meta.titulo.toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  })

  const kpis = {
    totalMetas: metas.length,
    metasAtivas: metas.filter((m) => m.status === 'ativa').length,
    metasEmRisco: metas.filter((m) => {
      const progresso = m.valor_meta > 0 ? (m.valor_atual / m.valor_meta) * 100 : 0
      return m.status === 'ativa' && progresso < 50
    }).length,
    percentualMedio: metas.length > 0
      ? metas.reduce((acc, m) => acc + (m.valor_meta > 0 ? (m.valor_atual / m.valor_meta) * 100 : 0), 0) / metas.length
      : 0,
  }

  const handleOpenModal = (meta?: Meta) => {
    if (meta) {
      setEditingMeta(meta)
      setFormData({
        titulo: meta.titulo,
        descricao: meta.descricao,
        metrica: meta.metrica,
        valor_meta: meta.valor_meta,
        periodo: meta.periodo,
        data_inicio: meta.data_inicio,
        data_fim: meta.data_fim,
        tipo_atribuicao: meta.tipo_atribuicao,
        linha_negocio: meta.linha_negocio,
        status: meta.status,
      })
    } else {
      setEditingMeta(null)
      setFormData({
        titulo: '',
        descricao: '',
        metrica: 'vendas',
        valor_meta: 0,
        periodo: 'mensal',
        data_inicio: '',
        data_fim: '',
        tipo_atribuicao: 'geral',
        linha_negocio: '',
        status: 'ativa',
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingMeta(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const payload = mapFormToApi(formData)

      if (editingMeta) {
        // Atualiza meta existente na API
        await api.updateMeta(editingMeta.id, payload)
      } else {
        // Cria nova meta na API
        await api.createMeta(payload)
      }

      // Recarrega lista da API para garantir sincronização
      await loadMetas()
      handleCloseModal()
    } catch (err) {
      setError(editingMeta ? 'Erro ao atualizar meta' : 'Erro ao criar meta')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta meta?')) return

    setSaving(true)
    setError(null)

    try {
      await api.deleteMeta(id)
      // Recarrega lista da API para garantir sincronização
      await loadMetas()
    } catch (err) {
      setError('Erro ao excluir meta')
    } finally {
      setSaving(false)
    }
  }

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return styles.success
    if (progress >= 50) return styles.warning
    return styles.danger
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Carregando metas...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Sistema de Metas</h1>
          <p className={styles.pageSubtitle}>Gerencie metas comerciais por empreendimento, corretor ou time</p>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div className={styles.errorBanner || styles.kpiCard} style={{ background: '#fee2e2', borderColor: '#ef4444', marginBottom: '1rem' }}>
          <p style={{ color: '#dc2626', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Indicador de salvamento */}
      {saving && (
        <div className={styles.savingBanner || styles.kpiCard} style={{ background: '#dbeafe', borderColor: '#3b82f6', marginBottom: '1rem' }}>
          <p style={{ color: '#1d4ed8', margin: 0 }}>Salvando...</p>
        </div>
      )}

      {/* KPIs */}
      <section className={styles.kpiGrid}>
        <div className={styles.kpiCard}>
          <div className={`${styles.kpiIcon} ${styles.primary}`}>
            <Target size={24} />
          </div>
          <div className={styles.kpiContent}>
            <span className={styles.kpiLabel}>Total de Metas</span>
            <span className={styles.kpiValue}>{kpis.totalMetas}</span>
          </div>
        </div>

        <div className={styles.kpiCard}>
          <div className={`${styles.kpiIcon} ${styles.success}`}>
            <CheckCircle size={24} />
          </div>
          <div className={styles.kpiContent}>
            <span className={styles.kpiLabel}>Metas Ativas</span>
            <span className={styles.kpiValue}>{kpis.metasAtivas}</span>
          </div>
        </div>

        <div className={styles.kpiCard}>
          <div className={`${styles.kpiIcon} ${styles.warning}`}>
            <AlertTriangle size={24} />
          </div>
          <div className={styles.kpiContent}>
            <span className={styles.kpiLabel}>Em Risco</span>
            <span className={styles.kpiValue}>{kpis.metasEmRisco}</span>
          </div>
        </div>

        <div className={styles.kpiCard}>
          <div className={`${styles.kpiIcon} ${styles.secondary}`}>
            <BarChart3 size={24} />
          </div>
          <div className={styles.kpiContent}>
            <span className={styles.kpiLabel}>% Medio Atingido</span>
            <span className={styles.kpiValue}>{kpis.percentualMedio.toFixed(0)}%</span>
          </div>
        </div>
      </section>

      {/* Filtros e Acoes */}
      <section className={styles.toolbar}>
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Status</label>
            <select
              className={styles.filterSelect}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="ativa">Ativa</option>
              <option value="pausada">Pausada</option>
              <option value="concluida">Concluida</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Metrica</label>
            <select
              className={styles.filterSelect}
              value={filterMetrica}
              onChange={(e) => setFilterMetrica(e.target.value)}
            >
              <option value="">Todas</option>
              <option value="vendas">Vendas</option>
              <option value="leads">Leads</option>
              <option value="pre_cadastros">Pre-Cadastros</option>
              <option value="reservas">Reservas</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Periodo</label>
            <select
              className={styles.filterSelect}
              value={filterPeriodo}
              onChange={(e) => setFilterPeriodo(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="diaria">Diaria</option>
              <option value="semanal">Semanal</option>
              <option value="mensal">Mensal</option>
              <option value="trimestral">Trimestral</option>
              <option value="anual">Anual</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Buscar</label>
            <input
              type="text"
              className={styles.filterInput}
              placeholder="Titulo da meta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.actions}>
          <button className={styles.btnPrimary} onClick={() => handleOpenModal()}>
            <Plus size={16} />
            Nova Meta
          </button>
        </div>
      </section>

      {/* Lista de Metas */}
      <section className={styles.metasGrid}>
        {filteredMetas.length > 0 ? (
          filteredMetas.map((meta) => {
            const progress = meta.valor_meta > 0 ? (meta.valor_atual / meta.valor_meta) * 100 : 0

            return (
              <div key={meta.id} className={styles.metaCard}>
                <div className={styles.metaHeader}>
                  <div className={styles.metaInfo}>
                    <h3 className={styles.metaTitle}>{meta.titulo}</h3>
                    <div className={styles.metaTags}>
                      <span className={`${styles.statusBadge} ${styles[meta.status]}`}>
                        {statusLabels[meta.status]}
                      </span>
                      <span className={styles.metricBadge}>{metricLabels[meta.metrica]}</span>
                      <span className={styles.periodBadge}>{periodoLabels[meta.periodo]}</span>
                    </div>
                  </div>
                  <div className={styles.metaActions}>
                    <button className={styles.actionBtn} onClick={() => handleOpenModal(meta)}>
                      <Edit2 size={16} />
                    </button>
                    <button className={styles.actionBtn} onClick={() => handleDelete(meta.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className={styles.metaProgress}>
                  <div className={styles.progressHeader}>
                    <span className={styles.progressLabel}>Progresso</span>
                    <span className={styles.progressValue}>{progress.toFixed(0)}%</span>
                  </div>
                  <div className={styles.progressBar}>
                    <div
                      className={`${styles.progressFill} ${getProgressColor(progress)}`}
                      style={{ width: `${Math.min(100, progress)}%` }}
                    />
                  </div>
                  <div className={styles.progressDetails}>
                    <span>{meta.valor_atual.toLocaleString('pt-BR')} de {meta.valor_meta.toLocaleString('pt-BR')}</span>
                  </div>
                </div>

                <div className={styles.metaFooter}>
                  <span className={styles.metaDates}>
                    {new Date(meta.data_inicio).toLocaleDateString('pt-BR')} - {new Date(meta.data_fim).toLocaleDateString('pt-BR')}
                  </span>
                  {meta.linha_negocio && (
                    <span className={`${styles.linhaBadge} ${meta.linha_negocio === 'invest' ? styles.invest : styles.realiza}`}>
                      FAJ {meta.linha_negocio.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            )
          })
        ) : (
          <div className={styles.emptyState}>
            <Target size={64} strokeWidth={1} />
            <h3>Nenhuma meta encontrada</h3>
            <p>Crie sua primeira meta para comecar a acompanhar o progresso</p>
            <button className={styles.btnPrimary} onClick={() => handleOpenModal()}>
              Criar Meta
            </button>
          </div>
        )}
      </section>

      {/* Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {editingMeta ? 'Editar Meta' : 'Nova Meta'}
              </h2>
              <button className={styles.modalClose} onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>

            <form className={styles.modalBody} onSubmit={handleSubmit}>
              <div className={styles.formRow}>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label className={styles.formLabel}>Titulo *</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={formData.titulo}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    required
                    placeholder="Ex: Meta Vendas Janeiro 2026"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label className={styles.formLabel}>Descricao</label>
                  <textarea
                    className={styles.formInput}
                    rows={2}
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    placeholder="Descricao detalhada da meta (opcional)"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Tipo de Metrica *</label>
                  <select
                    className={styles.formInput}
                    value={formData.metrica}
                    onChange={(e) => setFormData({ ...formData, metrica: e.target.value })}
                    required
                  >
                    <option value="vendas">Vendas</option>
                    <option value="leads">Leads</option>
                    <option value="pre_cadastros">Pre-Cadastros</option>
                    <option value="reservas">Reservas</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Valor da Meta *</label>
                  <input
                    type="number"
                    className={styles.formInput}
                    value={formData.valor_meta}
                    onChange={(e) => setFormData({ ...formData, valor_meta: parseFloat(e.target.value) || 0 })}
                    required
                    min="1"
                    placeholder="Ex: 150"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Periodo *</label>
                  <select
                    className={styles.formInput}
                    value={formData.periodo}
                    onChange={(e) => setFormData({ ...formData, periodo: e.target.value })}
                    required
                  >
                    <option value="diaria">Diaria</option>
                    <option value="semanal">Semanal</option>
                    <option value="quinzenal">Quinzenal</option>
                    <option value="mensal">Mensal</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="semestral">Semestral</option>
                    <option value="anual">Anual</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Linha de Negocio</label>
                  <select
                    className={styles.formInput}
                    value={formData.linha_negocio}
                    onChange={(e) => setFormData({ ...formData, linha_negocio: e.target.value })}
                  >
                    <option value="">Todas</option>
                    <option value="invest">FAJ INVEST</option>
                    <option value="realiza">FAJ REALIZA</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Data Inicio *</label>
                  <input
                    type="date"
                    className={styles.formInput}
                    value={formData.data_inicio}
                    onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Data Fim *</label>
                  <input
                    type="date"
                    className={styles.formInput}
                    value={formData.data_fim}
                    onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button type="button" className={styles.btnSecondary} onClick={handleCloseModal} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className={styles.btnPrimary} disabled={saving}>
                  {saving ? 'Salvando...' : (editingMeta ? 'Salvar Alteracoes' : 'Criar Meta')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
